/**
 * FAIR persona topology — shared types + constants (DC-1648).
 *
 * The topology view (PersonaTopologyView) renders the per-org FAIR `personas`
 * table (the ONLY persona system since DC-1648) as a force-directed graph
 * with drag-to-assign governance assignments. These types replace the
 * retired governance-topology/types.ts (PistoiaPersona et al.) — the persona
 * shape is now the canonical `Persona` from client/types/triangulation.
 */

import type * as d3 from 'd3';
import type { Persona } from '../../../types/triangulation';

// =============================================================================
// EMPLOYEE + ASSIGNMENT TYPES (wire shapes from /api/employees + /api/governance/assignments)
// =============================================================================

export interface TopologyEmployee {
  employee_id: string;
  display_name: string;
  email: string;
  department?: string;
  job_title?: string;
  persona_id?: string | null;
  persona_name?: string | null;
  is_active: boolean;
}

export interface Assignment {
  assignment_id: string;
  persona_id: string;
  employee_id: string;
  employee_name: string;
  employee_email: string;
  journey_id: string;
  scope: string;
  assigned_by: string;
  assigned_at: string;
}

/** Per-employee rollup for the sidebar's assigned-roles chips. */
export interface EmployeeAssignmentInfo {
  employee_id: string;
  assignments: {
    persona_id: string;
    persona_name: string;
    primary_role: string;
  }[];
}

// =============================================================================
// D3 GRAPH TYPES
// =============================================================================

export interface TopologyNode extends d3.SimulationNodeDatum {
  id: string;
  persona: Persona;
  role: string;
  isBaseline: boolean;
  isCritical: boolean;
}

export interface TopologyLink extends d3.SimulationLinkDatum<TopologyNode> {
  source: string | TopologyNode;
  target: string | TopologyNode;
  type: 'related';
}

// =============================================================================
// ALERT HISTORY (DC-194 — wire shapes from /api/governance/alerts*)
// =============================================================================

export interface AlertHistoryEntry {
  audit_id: string;
  assignment_id: string;
  change_type: 'created' | 'updated' | 'status_changed' | 'deleted';
  changed_by: string;
  changed_at: string;
  persona_name?: string;
  employee_name?: string;
  alert_type: 'assignment_created' | 'assignment_updated' | 'assignment_revoked' | 'assignment_deleted' | 'role_gap';
  alert_severity: 'info' | 'warning' | 'error';
  alert_message: string;
}

export interface AlertSummary {
  total: number;
  last24Hours: { total: number; errors: number; warnings: number };
  last7Days: { total: number; errors: number; warnings: number };
  recentAlerts: AlertHistoryEntry[];
}

// =============================================================================
// CONSTANTS
// =============================================================================

/** DC-189 keyboard codes for graph navigation. */
export const KEYS = {
  ARROW_UP: 'ArrowUp',
  ARROW_DOWN: 'ArrowDown',
  ARROW_LEFT: 'ArrowLeft',
  ARROW_RIGHT: 'ArrowRight',
  ENTER: 'Enter',
  SPACE: ' ',
  ESCAPE: 'Escape',
  TAB: 'Tab',
  HOME: 'Home',
  END: 'End',
} as const;

/** Role anchor positions for the forceX/forceY clustering hint. */
export const ROLE_POSITIONS: Record<string, { x: number; y: number }> = {
  'Data Producer': { x: 0.2, y: 0.3 },
  'Data Enabler': { x: 0.5, y: 0.7 },
  'Data Consumer': { x: 0.8, y: 0.3 },
};

/** More than 3 roles on one person is a concentration risk (DC-187). */
export const BUS_FACTOR_THRESHOLD = 3;

/**
 * Role → scoped CSS class. Colors live in persona-topology.css as
 * var(--tri-*) rules — never set fill/stroke hex from JS (the cream-layer
 * hex guard protects this directory).
 */
export function roleClassOf(role: string | null | undefined): string {
  switch (role) {
    case 'Data Producer': return 'role-producer';
    case 'Data Enabler': return 'role-enabler';
    case 'Data Consumer': return 'role-consumer';
    default: return 'role-unknown';
  }
}
